import { Warning } from '@mui/icons-material';
import { Box, Checkbox, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MAIN_NODES, PERMISSIONS_ACTIONS, PERMISSIONS_OPTIONS } from "../../../common/config/permissions";
import { useAuth } from '../../../common/hooks/useAuth';
import { IRole } from "../../../common/interfaces/roles";

interface IPermissionDialogProps {
    currentRole: IRole | undefined,
    setCurrentRoleBody: Dispatch<SetStateAction<Partial<IRole> | undefined>>,
    mainNodes: MAIN_NODES[],
    actions: PERMISSIONS_ACTIONS[],
    isDefaultRolePresent: boolean,
    saveBtnClicked: boolean
}

const PermissionsDialog: FC<IPermissionDialogProps> = (props) => {
    const { currentRole, setCurrentRoleBody, mainNodes, actions, isDefaultRolePresent, saveBtnClicked } = props
    const { t } = useTranslation();
    const { permissionsState } = useAuth();
    const [name, setName] = useState<string>("")
    const [isDefaultForRegistration, setIsDefaultForRegistration] = useState<boolean>(false);
    const [permissions, setPermissions] = useState<{ [x: string]: string[] } | undefined>(undefined);

    const setOptions = (mainNode: MAIN_NODES, action: PERMISSIONS_ACTIONS) => {
        if (
            (mainNode === MAIN_NODES.BEEHIVES && action !== PERMISSIONS_ACTIONS.MASSIVE_DOWNLOAD) ||
            mainNode === MAIN_NODES.BEEHIVES_STATS && action === PERMISSIONS_ACTIONS.CREATE ||
            mainNode === MAIN_NODES.BEEHIVES_ALERTS && (action === PERMISSIONS_ACTIONS.VIEW || action === PERMISSIONS_ACTIONS.CREATE)
        )
            return [
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.NO,
                    desc: t("permissionsPage.permissionsForm.noOption")
                },
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ONLY_OWNED,
                    desc: t("permissionsPage.permissionsForm.onlyOwnedOption")
                },
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ONLY_AREA,
                    desc: t("permissionsPage.permissionsForm.onlyAreaOption")
                },
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ALL,
                    desc: t("permissionsPage.permissionsForm.allOption")
                }
            ]
        else if (mainNode === MAIN_NODES.USERS)
            return [
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.NO,
                    desc: t("permissionsPage.permissionsForm.noOption")
                },
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ONLY_AREA,
                    desc: t("permissionsPage.permissionsForm.onlyAreaOption")
                },
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ALL,
                    desc: t("permissionsPage.permissionsForm.allOption")
                }
            ]
        else
            return [
                {
                    value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.NO,
                    desc: t("permissionsPage.permissionsForm.noOption")
                },
                {
                    value: mainNode + "_" + action,
                    desc: t("permissionsPage.permissionsForm.yesOption")
                }
            ]
    }

    const setLabel = (action: PERMISSIONS_ACTIONS) => {
        switch (action) {
            case PERMISSIONS_ACTIONS.VIEW:
                return t("permissionsPage.permissionsForm.view")
            case PERMISSIONS_ACTIONS.CREATE:
                return t("permissionsPage.permissionsForm.create")
            case PERMISSIONS_ACTIONS.EDIT:
                return t("permissionsPage.permissionsForm.edit")
            case PERMISSIONS_ACTIONS.DELETE:
                return t("permissionsPage.permissionsForm.delete")
            case PERMISSIONS_ACTIONS.DOWNLOAD:
                return t("permissionsPage.permissionsForm.download")
            case PERMISSIONS_ACTIONS.MASSIVE_DOWNLOAD:
                return t("permissionsPage.permissionsForm.massiveDownload")
            default:
                return ""
        }
    }

    const setPermissionsObject = (permissions: string[]) => {
        let savePermissions: { [x: string]: string[] } = {}
        let tempMainNode = ""
        permissions.map(permission => {
            let mainNode = permission.split("_")[0]
            Object.assign(savePermissions, {
                ...savePermissions,
                [mainNode]: mainNode === tempMainNode ? [...savePermissions[mainNode], permission] : [permission]
            })
            tempMainNode = mainNode
        })
        return savePermissions;
    }

    const setPermissionsArray = (permissions?: { [x: string]: string[] }) => {
        let savePermissions: string[] = []
        if (permissions) {
            Object.values(permissions).map(values => {
                savePermissions = [...savePermissions, ...values.filter(val => !val.includes("null"))]
            })
        }
        return savePermissions;
    }

    useEffect(() => {
        if (currentRole) {
            setName(currentRole.name)
            setIsDefaultForRegistration(currentRole.isDefaultForRegistration || false)
            setPermissions(setPermissionsObject(currentRole.permissions))
        } else {
            let perm: string[] = []
            mainNodes.map(mainNode =>
                perm = perm.concat(actions.map(act => mainNode + "_" + act + "_null"))
            )
            setPermissions(setPermissionsObject(perm))
        }
    }, [currentRole])

    useEffect(() => {
        setCurrentRoleBody({
            name,
            isDefaultForRegistration,
            permissions: setPermissionsArray(permissions)
        })
    }, [name, isDefaultForRegistration, permissions])

    const onSelectChange = (
        event: SelectChangeEvent<string>,
        options: {
            value: string;
            desc: string;
        }[],
        mainNode: string,
        action: string
    ) => {
        const findOption = options.find(opt => opt.value === event.target.value)
        let tempPermissions: { [x: string]: string[] } = { ...permissions }
        if (findOption && tempPermissions[mainNode]) {
            let findPermForAction = tempPermissions[mainNode].find(perm => perm.includes(mainNode + "_" + action))
            Object.assign(tempPermissions, {
                ...permissions,
                [mainNode]: [
                    ...tempPermissions[mainNode],
                    findOption.value
                ]
            })
            if (findOption.value.includes(mainNode + "_" + action) && findPermForAction) {
                let mainNodePermissions = tempPermissions[mainNode]
                mainNodePermissions.splice(tempPermissions[mainNode].indexOf(findPermForAction), 1)
                tempPermissions = {
                    ...permissions,
                    [mainNode]: [...mainNodePermissions]
                }
            }
        }
        if (findOption && !tempPermissions[mainNode]) {
            Object.assign(tempPermissions, {
                ...permissions,
                [mainNode]: [findOption.value]
            })
        }
        setPermissions(tempPermissions)
    }

    const setSelectField = (mainNode: MAIN_NODES, action: PERMISSIONS_ACTIONS, disabled: boolean) => {
        const options = setOptions(mainNode, action)
        const findPossibleValue = permissions && permissions[mainNode] && permissions[mainNode].length > 0 ?
            permissions[mainNode].find(perm => perm.includes(mainNode + "_" + action)) : mainNode + "_" + action + "_null"

        const value = findPossibleValue || mainNode + "_" + action + "_null"

        return <FormControl key={mainNode + "_" + currentRole?.id + "_" + action} size="small" sx={{ minWidth: "100%", mb: 2 }}>
            <InputLabel id={mainNode + "-" + action + "-select-label"}>
                {setLabel(action)}
            </InputLabel>
            <Select
                id={mainNode + "-" + action + "-select"}
                value={value}
                name={action}
                onChange={(event) => onSelectChange(event, options, mainNode, action)}
                labelId={mainNode + "-" + action + "-select-label"}
                label={setLabel(action)}
                disabled={disabled}
                MenuProps={{
                    style: {
                        maxHeight: 400,
                    },
                }}
            >
                {
                    options && options.map(
                        (option) => (
                            <MenuItem
                                key={option.value + "-" + option.desc}
                                value={option.value}
                            >
                                {option.desc}
                            </MenuItem>
                        )
                    )
                }
            </Select>
        </FormControl>
    }

    const actionsNoDownload = actions.filter(action => action !== PERMISSIONS_ACTIONS.DOWNLOAD && action !== PERMISSIONS_ACTIONS.MASSIVE_DOWNLOAD);

    return <Box sx={{ display: "flex", flexDirection: "column", px: 1 }}>
        <Box sx={{ mb: 2 }}>
            <TextField
                value={name}
                label={t("permissionsPage.permissionsForm.name")}
                onChange={(event) => setName(event.target.value)}
                size="small"
                sx={{ mt: 1, width: "100%" }}
                error={!name && saveBtnClicked}
                helperText={!name && saveBtnClicked ? t("permissionsPage.permissionsForm.requiredFieldError") : ""}
                required
            />
        </Box>
        <Box sx={{ mb: 2 }}>
            <FormControlLabel
                control={
                    <Checkbox
                        value={isDefaultForRegistration}
                        checked={isDefaultForRegistration}
                        onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => setIsDefaultForRegistration(checked)}
                        size="small"
                        disabled={isDefaultRolePresent && (!currentRole || (currentRole && !currentRole.isDefaultForRegistration))}
                    />
                }
                label={t("permissionsPage.permissionsForm.isDefaultForRegistration")}
            />
            {
                isDefaultRolePresent &&
                (!currentRole || (currentRole && !currentRole.isDefaultForRegistration)) &&
                <Typography variant="body2" sx={{ fontWeight: 500, display: "flex", alignItems: "center" }}>
                    <Warning color='primary' sx={{ mr: 0.5 }} />
                    {t("permissionsPage.permissionsForm.isDefaultRolePresent")}
                </Typography>
            }
        </Box>

        <Divider sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>
            {t("permissionsPage.permissionsForm.permissions")}
        </Typography>
        {
            mainNodes.map(node => {
                return <Grid key={node} item xs={12} sm={6} md={4} lg={3} xl={3}>
                    {
                        <>
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }} gutterBottom>
                                {node === MAIN_NODES.BEEHIVES && t("permissionsPage.sections.beehivesTitle")}
                                {node === MAIN_NODES.BEEHIVES_STATS && t("permissionsPage.sections.beehivesStatsTitle")}
                                {node === MAIN_NODES.BEEHIVES_ALERTS && t("permissionsPage.sections.beehivesAlertsTitle")}
                                {node === MAIN_NODES.USERS && t("permissionsPage.sections.usersTitle")}
                                {node === MAIN_NODES.ROLES && t("permissionsPage.sections.rolesTitle")}
                                {node === MAIN_NODES.AREAS_EXPERTISE && t("permissionsPage.sections.areasExpertiseTitle")}
                            </Typography>
                            {
                                (node === MAIN_NODES.BEEHIVES) && actions.map(action =>
                                    setSelectField(node, action, permissionsState && permissionsState.existViewBeehivesPermission ? false : true)
                                )
                            }
                            {
                                node !== MAIN_NODES.BEEHIVES && actionsNoDownload.map(action =>
                                    setSelectField(node, action, node === MAIN_NODES.BEEHIVES_ALERTS ? (permissionsState && permissionsState.existViewBeehivesPermission ? false : true) : false)
                                )
                            }
                        </>
                    }
                </Grid>
            })
        }
    </Box>
}

export default PermissionsDialog;